'use client';
import type { ReactNode } from 'react';
import type { InterfaceResourceStrategyType } from '../../../context/InterfaceResourceStrategyProvider/constants';
import type BlocksRegistry from '../../../context/RegistryProvider/BlocksRegistry';
import { useGetUserContext } from '../../../hooks/useGetUserContext';
import { InterfaceClient } from '../../../stores/InterfaceStore';
import { WalkthroughManagerStoreProvider } from '../../../stores/WalkthroughManagerStore';
import { WalkthroughMode } from '../../../stores/WalkthroughManagerStore/const';
import { WalkthroughGuide } from '../PageWalkthroughGuide';
import { WalkthroughManagerEffects } from '../WalkthroughManagerEffects';

interface InterfaceWalkthroughProvider {
  children: ReactNode;
  /**
   * Opaque identifier for this walkthrough host instance.
   * In no-code: pass the interface record id.
   * In generic hosts: pass any stable id (e.g. normalized pathname, route-template key).
   */
  instanceId: string;
  registry: BlocksRegistry;
  strategy: InterfaceResourceStrategyType;
  client?: InterfaceClient;
  mode?: WalkthroughMode;
}

// Whenever we want to add NoCode app somewhere, and we want to use walkthroughs, we need to wrap this provider above InterfacePageResourceLoader.
// This is single across an app instance i.e. for a use-case it is only once.
export function InterfaceWalkthroughProvider({
  children,
  instanceId,
  registry,
  strategy,
  client = InterfaceClient.UA_MATRIX,
  mode = client === InterfaceClient.UA_MATRIX ? WalkthroughMode.RUNNER : WalkthroughMode.PREVIEW,
}: InterfaceWalkthroughProvider) {
  const { userContext } = useGetUserContext();

  return (
    <WalkthroughManagerStoreProvider instanceId={instanceId} mode={mode} userId={userContext?.id}>
      <WalkthroughManagerEffects instanceId={instanceId} />
      {/* in builder mode we render the walkthrough guide closer to the standard page so that it can take the right space */}
      {mode === WalkthroughMode.RUNNER ? (
        <WalkthroughGuide client={client} registry={registry} strategy={strategy} />
      ) : null}
      {children}
    </WalkthroughManagerStoreProvider>
  );
}
